import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { NUMEROLOGY_ARTICLES } from '../../data/numerologyContent';
import { getCourseLessonAdjacency } from '../../data/numerologyCourse';
import {
  getLessonBlockVariant,
  parseInlineMarkdown,
  splitParagraphForReadability,
} from '../../lib/numerologyLessonRichText';
import '../../components/numerology/numerologyEditorial.css';
import '../../components/numerology/ammNumerologyEditorialBridge.css';

const LEARN_DEFAULT_TITLE = 'Numerology | Astral Moon Magic';

function RichParagraphs({ text }) {
  if (text == null) return null;
  const chunks = splitParagraphForReadability(text, 3);
  return (
    <>
      {chunks.map((chunk, idx) => (
        <p key={idx}>{parseInlineMarkdown(chunk)}</p>
      ))}
    </>
  );
}

function RichListItem({ text }) {
  return <li>{parseInlineMarkdown(text)}</li>;
}

export default function NumerologyLearnPage() {
  const { slug } = useParams();
  const article = NUMEROLOGY_ARTICLES[slug];
  const adjacency = slug ? getCourseLessonAdjacency(slug) : null;

  useEffect(() => {
    const prev = document.title;
    if (!article) {
      document.title = `Not found | ${LEARN_DEFAULT_TITLE}`;
    } else {
      document.title = `${article.title} | Numerology | Astral Moon Magic`;
    }
    return () => {
      document.title = prev;
    };
  }, [article, slug]);

  if (!article) {
    return (
      <div className="amm-num-edit">
        <div className="amm-num-edit-inner">
          <div className="amm-num-edit-shell">
            <header className="amm-num-edit-open">
              <p className="amm-num-edit-lead">This page could not be found.</p>
              <Link className="amm-num-edit-btn amm-num-edit-btn--primary" to="/numerology/study">
                Back to course
              </Link>
            </header>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="amm-num-edit">
      <div className="amm-num-edit-inner">
        <article className="amm-num-edit-shell">
          <header className="amm-num-edit-open">
            <p className="amm-num-edit-eyebrow">Astral Moon Magic · Numerology course</p>
            <h1 className="amm-num-edit-h1">{article.title}</h1>
            <div className="amm-num-edit-lead amm-num-edit-lead--rich">
              <RichParagraphs text={article.intro} />
            </div>
          </header>

          <div className="amm-num-edit-prose">
            {article.blocks?.map((block, i) => {
              const variant = getLessonBlockVariant(block.h2);
              const h2Class =
                variant !== 'default' ? `amm-num-edit-h2 amm-num-edit-h2--${variant}` : 'amm-num-edit-h2';
              const blockHeadingId = block.h2
                ? block.h2.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                : undefined;

              return (
                <section
                  key={i}
                  className={`amm-num-edit-block amm-num-edit-block--${variant}`}
                  aria-labelledby={blockHeadingId}
                >
                  {block.h2 ? (
                    <h2 className={h2Class} id={blockHeadingId}>
                      {block.h2}
                    </h2>
                  ) : null}
                  <div className="amm-num-edit-block__body">
                    {block.p?.map((para, j) => (
                      <RichParagraphs key={j} text={para} />
                    ))}
                    {block.ol ? (
                      <ol className="amm-num-edit-ol">
                        {block.ol.map((item, k) => (
                          <RichListItem key={k} text={item} />
                        ))}
                      </ol>
                    ) : null}
                    {block.ul ? (
                      <ul className="amm-num-edit-list">
                        {block.ul.map((item, k) => (
                          <RichListItem key={k} text={item} />
                        ))}
                      </ul>
                    ) : null}
                    {block.sections?.map((sec, k) => (
                      <div key={k} className="amm-num-edit-subsection">
                        {sec.h3 ? <h3 className="amm-num-edit-h3">{sec.h3}</h3> : null}
                        {sec.p?.map((para, j) => (
                          <RichParagraphs key={j} text={para} />
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          <nav className="amm-num-edit-lesson-nav" aria-label="Lesson navigation">
            {adjacency ? (
              <>
                <div className="amm-num-edit-lesson-nav__actions">
                  {adjacency.prev ? (
                    <Link
                      className="amm-num-edit-btn"
                      to={adjacency.prev.href}
                      aria-label={`Previous lesson: ${adjacency.prev.label}`}
                    >
                      ← {adjacency.prev.label}
                    </Link>
                  ) : null}
                  {adjacency.next ? (
                    <Link
                      className="amm-num-edit-btn amm-num-edit-btn--primary"
                      to={adjacency.next.href}
                      aria-label={`Next lesson: ${adjacency.next.label}`}
                    >
                      {adjacency.next.label} →
                    </Link>
                  ) : null}
                </div>
                <p className="amm-num-edit-back-to-module">
                  <Link className="amm-num-edit-back-to-module-link" to={`/numerology/study/${adjacency.module.path}`}>
                    Back to module · {adjacency.module.title}
                  </Link>
                </p>
              </>
            ) : (
              <p className="amm-num-edit-back-to-module">
                <Link className="amm-num-edit-back-to-module-link" to="/numerology/study">
                  Back to course
                </Link>
              </p>
            )}
          </nav>
        </article>
      </div>
    </div>
  );
}
